import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only settings panel — currently exposes the founder / notification inbox override.
 * When set, all owner-side notifications (new waitlist signups, new cert applications, diagnostic sends)
 * route to this address instead of the environment default.
 */
export default function OwnerEmailSetting() {
  const [value, setValue] = useState("");
  const [current, setCurrent] = useState("");
  const [defaultAddr, setDefaultAddr] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setValue(r.data.owner_email || "");
      setCurrent(r.data.owner_email || "");
      setDefaultAddr(r.data.default || "");
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings/owner-email", { value });
      setCurrent(value);
      toast.success("Notification email updated");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/test-email");
      toast.success(`Test email sent to ${r.data?.sent_to || "owner"} — check inbox and spam/promotions`);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setTesting(false); }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="owner-email-setting">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Notifications</div>
          <h3 className="mt-1 font-serif text-xl text-[#0A1128]">Founder inbox</h3>
          <p className="mt-1 text-sm text-slate-500">
            Owner notifications (new waitlist signups, new certification applications, diagnostic sends) route here. Use any provider — Gmail, Yahoo, iCloud, Outlook, your business domain, etc.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <Input
              type="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="you@example.com"
              className="max-w-sm"
              required
              data-testid="owner-email-input"
            />
            <Button type="submit" disabled={saving || !value || value === current}
              data-testid="owner-email-save"
              className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button type="button" variant="outline" onClick={test} disabled={testing}
              data-testid="owner-email-test"
              className="rounded-full">
              <Mail className="w-4 h-4 mr-1" /> {testing ? "Sending…" : "Send test to this inbox"}
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
            <span>Currently active: <b className="text-[#0A1128]">{current || "—"}</b></span>
            {defaultAddr && current !== defaultAddr && (
              <span>· Env default: {defaultAddr}</span>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
